/* eslint-disable max-lines-per-function */
import {defineCustomElement, formatPrice, joinUrl} from 'custom-extensions/utils';
import GTM from 'extensions/gtm';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {ref} from 'lit/directives/ref.js';
import styles from './x-product-card.component.scss?as=stylesheet';

const NO_IMAGE_THUMBNAIL = window.mivaJS.NO_IMAGE_THUMBNAIL;

export class ProductCard extends LitElement {
	static styles = [styles];

	static get properties () {
		return {
			attributeCount: {
				attribute: 'attribute-count',
				type: Number
			},
			basePrice: {
				attribute: 'base-price',
				type: Number
			},
			code: {
				type: String
			},
			gtmList: {
				attribute: 'data-gtm-list',
				type: String
			},
			id: {
				attribute: 'product-id',
				type: String
			},
			imageAlt: {
				attribute: 'image-alt',
				type: String
			},
			imageHeight: {
				attribute: 'image-height',
				type: Number
			},
			imageUrl: {
				attribute: 'image-url',
				type: String
			},
			imageWidth: {
				attribute: 'image-width',
				type: Number
			},
			klevu: {
				type: Boolean
			},
			name: {
				type: String
			},
			placeholder: {
				type: Boolean
			},
			price: {
				type: Number
			},
			priceHigh: {
				attribute: 'price-high',
				type: Number
			},
			priceLow: {
				attribute: 'price-low',
				type: Number
			},
			productFlagImage: {
				attribute: 'product-flag-image',
				type: String
			},
			productFlagPosition: {
				attribute: 'product-flag-position',
				type: String
			},
			showAddToCart: {
				attribute: 'add-to-cart',
				type: Boolean
			},
			sourceLoaded: {
				state: true,
				type: Boolean
			},
			tabindex: {
				type: Number
			},
			url: {
				type: String
			}
		};
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		// Default property values

		/**
		 * @type {Number}
		 */
		this.attributeCount = 0;

		/**
		 * @type {Number}
		 */
		this.basePrice = 0;

		/**
		 * @type {String}
		 */
		this.code = '';

		/**
		 * @type {String}
		 */
		this.gtmList = '';

		/**
		 * @type {String}
		 */
		this.id = '';

		/**
		 * @type {String}
		 */
		this.imageAlt = '';

		/**
		 * @type {Number}
		 */
		this.imageHeight = 360;

		/**
		 * @type {String}
		 */
		this.imageUrl = window.mivaJS?.NO_IMAGE_THUMBNAIL || '';

		/**
		 * @type {Number}
		 */
		this.imageWidth = 360;

		/**
		 * @type {Boolean}
		 */
		this.klevu = false;

		/**
		 * @type {Boolean}
		 */
		this.placeholder = false;

		/**
		 * @type {String}
		 */
		this.name = '';

		/**
		 * @type {Number}
		 */
		this.price = 0;

		/**
		 * @type {Number}
		 */
		this.priceHigh = 0;

		/**
		 * @type {Number}
		 */
		this.priceLow = 0;

		/**
		 * @type {String}
		 */
		this.productFlagImage = '';

		/**
		 * @type {String}
		 */
		this.productFlagPosition = 'Top Left';

		/**
		 * @type {Boolean}
		 */
		this.showAddToCart = false;

		/**
		 * @type {Boolean}
		 */
		this.sourceLoaded = false;

		/**
		 * @type {Number}
		 */
		this.tabindex = 0;

		/**
		 * @type {String}
		 */
		this.url = '';
	}

	get formattedBasePrice () {
		return formatPrice(this.basePrice);
	}

	get formattedPrice () {
		return formatPrice(this.price);
	}

	get formattedPriceLow () {
		return formatPrice(this.priceLow);
	}

	get formattedPriceHigh () {
		return formatPrice(this.priceHigh);
	}

	get formattedUrl () {
		const baseUrl = this.url;

		return joinUrl(baseUrl, [
			...this.gtmList
				? [
					// eslint-disable-next-line array-element-newline
					['list', this.gtmList]
				]
				: []
		]);
	}

	handleSourceLoad () {
		this.sourceLoaded = true;
	}

	handleImageError (e) {
		const [target] = e.composedPath();

		target.src = NO_IMAGE_THUMBNAIL;
	}

	imageContainerChanged (container) {
		if (!container) {
			return;
		}

		const observer = new MutationObserver((changes) => {
			changes.forEach(change => {
				if (change.attributeName.includes('src')) {
					this.sourceLoaded = container.complete;
				}
			});
		});

		observer.observe(container, {attributes: true});
	}

	renderPrice () {
		if (this.priceLow > 0 && this.priceHigh > 0) {
			return html`
				<span>${this.formattedPriceLow} <span class="pricing-sub-text">to</span> ${this.formattedPriceHigh}</span>
			`;
		}

		if (this.priceLow) {
			return html`
				<span><span class="pricing-sub-text">Starting at</span> ${this.formattedPriceLow}</span>
			`;
		}

		return html`
			<span>${this.formattedPrice}</span>
			${this.basePrice > this.price ? html`<s>${this.formattedBasePrice}</s>` : nothing}
		`;
	}

	handleClick () {
		if (this.placeholder) {
			return;
		}

		this.gtm.sendProductClick({
			/* eslint-disable camelcase */
			item_id: this.code,
			item_list_id: this.gtmList,
			item_list_name: this.gtmList,
			item_name: this.name,
			/* eslint-enable camelcase */
			price: this.price
		});

		if (this.klevu) {
			this.klevuClick();
		}
	}

	klevuClick () {
		if (!window.mivaJS?.Klevu_API_Key) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);

		fetch('https://stats.ksearchnet.com/analytics/productTracking?' + new URLSearchParams({
			/* eslint-disable camelcase */
			klevu_apiKey: window.mivaJS.Klevu_API_Key,
			klevu_keywords: urlParams.get('q'),
			klevu_productId: this.id,
			klevu_productName: this.name,
			klevu_productUrl: this.url,
			klevu_type: 'clicked'
			/* eslint-enable camelcase */
		}));
	}

	render () {
		const containerClasses = {
			container: true,
			'image-loaded': this.sourceLoaded,
			placeholder: this.placeholder
		};

		return html`
			<a href="${this.formattedUrl}" @click="${this.handleClick}" class="${classMap(containerClasses)}" tabindex="${this.tabindex}">
				<div class="image-container">
					<span class="image-loader"></span>
					<img ${ref(this.imageContainerChanged)}
						src="${this.imageUrl}"
						alt="${this.imageAlt || this.name}"
						class="image"
						loading="lazy"
						height="${this.imageHeight}"
						width="${this.imageWidth}"
						@load="${this.handleSourceLoad}"
						@error="${this.handleImageError}"
					/>
					${this.renderProductFlag()}
				</div>
				<div class="information-container">
					<div class="name">
						<span class="name-text">
							${this.name}
						</span>
					</div>
					<div class="pricing">
						<span class="pricing-text">${this.renderPrice()}</span>
					</div>
				</div>
			</a>
			${this.renderButtons()}
		`;
	}

	renderProductFlag () {
		if (!this.productFlagImage) {
			return nothing;
		}

		const classes = {
			'product-flag': true,
			'product-flag--bottom': this.productFlagPosition?.toLowerCase?.()?.includes?.('bottom'),
			'product-flag--left': this.productFlagPosition?.toLowerCase?.()?.includes?.('left'),
			'product-flag--right': this.productFlagPosition?.toLowerCase?.()?.includes?.('right'),
			'product-flag--top': this.productFlagPosition?.toLowerCase?.()?.includes?.('top')
		};

		return html`
			<span class="${classMap(classes)}">
				<img src="${this.productFlagImage}" class="product-flag-image" alt="" loading="lazy" />
			</span>
		`;
	}

	renderButtons () {
		if (!this.showAddToCart) {
			return nothing;
		}

		if (this.attributeCount > 0) {
			return html`
				<div class="add-to-cart-wrapper">
					<a href="${this.formattedUrl}" @click="${this.handleClick}" class="g-button-secondary g-button-width--full add-to-cart-button" tabindex="${this.tabindex}">
						Choose Options
					</a>
				</div>
			`;
		}

		return html`
			<form method="POST" action="${window.mivaJS?.Basket_Url}" class="add-to-cart-form add-to-cart-wrapper">
				<input type="hidden" name="Action" value="ADPR" />
				<input type="hidden" name="Product_Code" value="${this.code}" />
				<input type="hidden" name="Quantity" value="1" />
				<button type="submit" class="g-button-secondary g-button-width--full add-to-cart-button" tabindex="${this.tabindex}">Add to Cart</button>
			</form>
		`;
	}
};

defineCustomElement('x-product-card', ProductCard);
